import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchContextProps, SearchItem } from './types'
import { searchableItems } from './data'
import { buildSearchIndex } from './buildSearchIndex'
import { useTickers } from 'api/herd-power/fetchHerdPowerTickers'
import { useBotStats } from 'api/mavbots-arena/fetchBotStats'
import { DollarIcon, MavBotsIcon } from 'assets/icons/icons'

// Maximum number of tickers to load initially
const INITIAL_TICKER_BATCH_SIZE = 500

// Initialize search index from searchableItems - these are static and don't need the API
const initialSearchIndex = buildSearchIndex(searchableItems)

// Create context with default values
const SearchContext = createContext<SearchContextProps>({
	isOpen: false,
	openSearch: () => {},
	closeSearch: () => {},
	searchIndex: initialSearchIndex,
	setSearchIndex: () => {},
	isLoading: false
})

// Custom hook to use the search context
export const useSearch = () => useContext(SearchContext)

interface SearchProviderProps {
	children: ReactNode
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchIndex, setSearchIndex] = useState<SearchItem[]>(initialSearchIndex)
	const [isInitialLoad, setIsInitialLoad] = useState(true)
	const [isLoading, setIsLoading] = useState(false)

	// Only fetch tickers when the search is open to improve performance
	const { data: tickers, isSuccess: isTickersSuccess, isLoading: isTickersLoading } = useTickers({
		enabled: isOpen
	})

	// Fetch bot data when search is open
	const { data: botData, isLoading: isBotsLoading } = useBotStats({
		enabled: isOpen, // Only fetch when search is open
		staleTime: 1000 * 60 * 30 // 30 minutes
	})

	// Update loading state based on data fetching
	useEffect(() => {
		setIsLoading(isTickersLoading || isBotsLoading)
	}, [isTickersLoading, isBotsLoading])

	// Memoize the ticker items conversion to prevent unnecessary recalculations
	const tickerItems = useMemo(() => {
		if (!isTickersSuccess || !tickers) return []

		// Convert tickers to SearchItem format - limit initial load for better performance
		const tickersToProcess = isInitialLoad ? tickers.slice(0, INITIAL_TICKER_BATCH_SIZE) : tickers

		return tickersToProcess.map(ticker => ({
			id: ticker.ticker,
			label: `${ticker.company} (${ticker.ticker})`,
			to: `/decoder/navigator/${ticker.ticker}`,
			icon: DollarIcon as any, // Fix type issue
			type: 'Stock',
			section: 'Stocks',
			keywords: [ticker.ticker, ticker.company, 'stock']
		}))
	}, [isTickersSuccess, tickers, isInitialLoad])

	// Memoize the bot items conversion
	const botItems = useMemo(() => {
		if (!botData) return []

		return botData.map(bot => ({
			id: `bot-${bot.bot_id}`,
			label: bot.bot_name,
			// No action needed here since we're handling it in MavFinder.tsx handleSelect
			icon: MavBotsIcon as any, // Fix type issue
			type: 'Bot' as const,
			section: 'Arena',
			botData: bot, // Store the full bot data for use in handleBotClick
			keywords: [bot.bot_name, 'bot', 'trader', bot.role, ...bot.trades, bot.strategy].filter(Boolean) as string[]
		}))
	}, [botData])

	// Load all results when user starts searching
	useEffect(() => {
		if (isOpen && isInitialLoad && isTickersSuccess) {
			// After initial render, mark as no longer initial load so we can load all tickers next time
			setIsInitialLoad(false)
		}
	}, [isOpen, isInitialLoad, isTickersSuccess])

	// Update search index when ticker items change
	useEffect(() => {
		if (tickerItems.length > 0) {
			// Use functional update to ensure we have the latest state
			setSearchIndex(currentIndex => {
				// Filter out any existing ticker items (in case this is a refresh)
				const baseItems = currentIndex.filter(item => item.type !== 'Stock')
				return [...baseItems, ...tickerItems]
			})
		}
	}, [tickerItems])

	// Update search index when bot items change
	useEffect(() => {
		if (botItems.length > 0) {
			// Use functional update to ensure we have the latest state
			setSearchIndex(currentIndex => {
				// Filter out any existing bot items (in case this is a refresh)
				const baseItems = currentIndex.filter(item => item.type !== 'Bot')
				return [...baseItems, ...botItems]
			})
		}
	}, [botItems])

	// Load more data when search is opened for subsequent times
	useEffect(() => {
		if (isOpen && !isInitialLoad && isTickersSuccess && tickers) {
			// Load remaining tickers after initial render
			const remainingTickers = tickers.slice(INITIAL_TICKER_BATCH_SIZE)

			if (remainingTickers.length > 0) {
				setIsLoading(true)

				// Use a small delay to allow UI to render first
				const timerId = setTimeout(() => {
					const remainingItems = remainingTickers.map(ticker => ({
						id: ticker.ticker,
						label: `${ticker.company} (${ticker.ticker})`,
						to: `/decoder/navigator/${ticker.ticker}`,
						icon: DollarIcon as any,
						type: 'Stock',
						section: 'Stocks',
						keywords: [ticker.ticker, ticker.company, 'stock']
					}))

					setSearchIndex(currentIndex => {
						// Only add items that don't already exist
						const existingIds = new Set(currentIndex.map(item => item.id))
						const newItems = remainingItems.filter(item => !existingIds.has(item.id))
						return [...currentIndex, ...newItems]
					})

					setIsLoading(false)
				}, 300)

				return () => clearTimeout(timerId)
			}
		}
	}, [isOpen, isInitialLoad, isTickersSuccess, tickers])

	// Optimize keyboard event handler with useCallback
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			// Check for Cmd+K or Ctrl+K
			if ((event.metaKey || event.ctrlKey) && event.key === 'm') {
				event.preventDefault()
				setIsOpen(prev => !prev)
			}

			// Close on Escape key
			if (event.key === 'Escape' && isOpen) {
				setIsOpen(false)
			}
		},
		[isOpen]
	)

	// Handle keyboard shortcuts
	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [handleKeyDown])

	const openSearch = useCallback(() => setIsOpen(true), [])
	const closeSearch = useCallback(() => setIsOpen(false), [])

	// Memoize context value to prevent unnecessary re-renders
	const contextValue = useMemo(
		() => ({
			isOpen,
			openSearch,
			closeSearch,
			searchIndex,
			setSearchIndex,
			isLoading
		}),
		[isOpen, openSearch, closeSearch, searchIndex, isLoading]
	)

	return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>
}

export default SearchContext
